import type { GeometryElement, Point } from 'jsxgraph';
import {
  bindGrabHandle,
  bindLabel,
  boundingBoxToTuple,
  createElementFactory,
  JsxgraphBaseWidget,
  type BoundGrabHandle,
  type BoundLabel,
  type CurveArgs,
  type JsxgraphStatic,
  type JsxgraphThemeService,
  type ResolvedLabelConfig,
  type WidgetState,
} from '@examdojo/core/widget';
import { compileExpression } from './expression.compile';
import type { FunctionTestCurve, FunctionTestWidgetConfig } from './function-test.model';
import {
  computeHorizontalIntersections,
  computeIntersections,
  isCurveAFunction,
  isCurveOneToOne,
} from './intersection.compute';

const MAX_INTERSECTION_POINTS = 20;

/**
 * Compile a JSON-serializable FunctionTestCurve into a CurveArgs for the element factory.
 * Expression strings are compiled into callable functions at runtime.
 */
function compileCurve(curve: FunctionTestCurve): CurveArgs {
  switch (curve.type) {
    case 'expression': {
      const fn = compileExpression(curve.expression, 'x');
      return {
        type: 'functiongraph',
        fn,
        domain: curve.domain,
        style: { color: 'sky-500' },
      };
    }
    case 'parametric': {
      const xFn = compileExpression(curve.x, 't');
      const yFn = compileExpression(curve.y, 't');
      return {
        type: 'parametric',
        x: xFn,
        y: yFn,
        range: curve.range,
        style: { color: 'sky-500' },
      };
    }
    case 'data': {
      return {
        type: 'data',
        xData: curve.xData,
        yData: curve.yData,
        style: { color: 'sky-500' },
      };
    }
  }
}

export class FunctionTestWidget extends JsxgraphBaseWidget<FunctionTestWidgetConfig> {
  constructor(container: HTMLElement, jsxInstance: JsxgraphStatic, themeService: JsxgraphThemeService) {
    super(container, jsxInstance, themeService);
    this.id = `function-test-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  }

  readonly id: string;
  readonly type = 'function-test';

  private config?: FunctionTestWidgetConfig;

  // JSXGraph elements
  private curveElement?: GeometryElement;
  private lineElement?: GeometryElement;
  private anchorPoint?: GeometryElement;
  private grabHandle?: BoundGrabHandle;
  private intersectionPoints: GeometryElement[] = [];
  private feedbackLabel?: BoundLabel;
  private countLabelAnchor?: GeometryElement;
  private countLabel?: BoundLabel;

  // Compiled curve (CurveArgs with live functions) for rendering + intersection computation
  private compiledCurveArgs?: CurveArgs;

  // State
  private mode: 'vlt' | 'hlt' = 'vlt';
  private currentLineX = 0;
  private currentLineY = 0;
  private isFunction = true;

  protected getCurrentConfig(): FunctionTestWidgetConfig | undefined {
    return this.config;
  }

  getState(): WidgetState {
    return {
      type: this.type,
      id: this.id,
      ...(this.mode === 'hlt' ? { lineY: this.currentLineY } : { lineX: this.currentLineX }),
    };
  }

  init(config: FunctionTestWidgetConfig): void {
    this.config = config;
    const { params, display } = config;

    // 1. Board + coordinate system
    const coordinateSystem = display?.coordinateSystem;
    let boundingbox = boundingBoxToTuple(
      coordinateSystem?.boundingbox ?? { xStart: -10, xEnd: 10, yStart: -8, yEnd: 8 },
    );

    const fixedAxis = coordinateSystem?.fixedAxis;
    if (fixedAxis) {
      boundingbox = this.adjustBboxForFixedAxis(boundingbox, fixedAxis);
    }

    this.initBoard({ boundingbox, axis: false });

    if (fixedAxis) {
      this.setupFixedAxisObserver();
    }

    this.createCoordinateSystem(coordinateSystem);

    // 2. Element factory
    const elementFactory = createElementFactory(this.board, this.container, this.themeService, {
      defaultPrecision: display?.math?.defaultPrecision,
    });

    const ctx = elementFactory.getContext();

    // 3. Compile expression-based curve into CurveArgs with live functions
    this.compiledCurveArgs = compileCurve(params.curve);

    // 4. Determine mode and global test result
    this.mode = params.mode ?? 'vlt';
    const isHLT = this.mode === 'hlt';

    this.isFunction = isHLT
      ? isCurveOneToOne(this.compiledCurveArgs, this.board)
      : isCurveAFunction(this.compiledCurveArgs);

    // 5. Resolve colors
    const feedbackColorName = this.isFunction ? 'sky' : 'pink';
    const neutralColor = this.themeService.resolveColorToken('neutral-400');

    // 6. Create the curve via element factory (with global color)
    this.compiledCurveArgs.style = { ...this.compiledCurveArgs.style, color: this.isFunction ? 'sky-500' : 'pink-500' };
    const curveResult = elementFactory.curve(this.compiledCurveArgs);
    this.curveElement = curveResult.curve;
    this.objects.set('curve', this.curveElement);

    // 7. Create the test line (vertical for VLT, horizontal for HLT)
    if (isHLT) {
      this.currentLineY = 0;
      const linePointA = this.board.create('point', [() => boundingbox[0] - 20, () => this.currentLineY], {
        visible: false,
        fixed: true,
      }) as GeometryElement;
      const linePointB = this.board.create('point', [() => boundingbox[2] + 20, () => this.currentLineY], {
        visible: false,
        fixed: true,
      }) as GeometryElement;

      this.lineElement = this.board.create('line', [linePointA, linePointB], {
        strokeColor: neutralColor,
        strokeWidth: 2,
        dash: 3,
        straightFirst: true,
        straightLast: true,
        fixed: true,
        highlight: false,
        withLabel: false,
      }) as GeometryElement;
    } else {
      this.currentLineX = 0;
      const linePointA = this.board.create('point', [() => this.currentLineX, () => boundingbox[3] - 20], {
        visible: false,
        fixed: true,
      }) as GeometryElement;
      const linePointB = this.board.create('point', [() => this.currentLineX, () => boundingbox[1] + 20], {
        visible: false,
        fixed: true,
      }) as GeometryElement;

      this.lineElement = this.board.create('line', [linePointA, linePointB], {
        strokeColor: neutralColor,
        strokeWidth: 2,
        dash: 3,
        straightFirst: true,
        straightLast: true,
        fixed: true,
        highlight: false,
        withLabel: false,
      }) as GeometryElement;
    }
    this.objects.set('line', this.lineElement);

    // 8. Anchor point for grab handle
    //    The grab handle SVG (60px) is offset downward from the anchor by size*1.05 (~63px).
    //    Convert that pixel offset to coordinate units and push the anchor up by that amount.
    const coordHeight = boundingbox[1] - boundingbox[3];
    const containerHeight = this.container.getBoundingClientRect().height || 1;
    const handleOffsetCoords = 60 * 1.05 * (coordHeight / containerHeight);

    if (isHLT) {
      // Position anchor so the handle (60px wide, centered) sits inside the right edge with padding.
      // 30px = half handle width, 12px = breathing room from canvas edge.
      const coordWidth = boundingbox[2] - boundingbox[0];
      const containerWidth = this.container.getBoundingClientRect().width || 1;
      const handleRightPadding = 42 * (coordWidth / containerWidth);
      const xAnchor = boundingbox[2] - handleRightPadding;
      this.anchorPoint = this.board.create('point', [xAnchor, () => this.currentLineY], {
        visible: false,
        fixed: true,
      }) as GeometryElement;
    } else {
      const yAnchor = boundingbox[3] + handleOffsetCoords;
      this.anchorPoint = this.board.create('point', [() => this.currentLineX, yAnchor], {
        visible: false,
        fixed: true,
      }) as GeometryElement;
    }

    // 9. Grab handle — axis lock and drag direction depend on mode
    this.grabHandle = bindGrabHandle({
      ctx,
      point: this.anchorPoint as unknown as Point,
      color: neutralColor,
      axis: isHLT ? 'y' : 'x',
      onDrag: isHLT
        ? (_dx, dy) => {
            this.currentLineY += dy;
            this.updateIntersections();
          }
        : (dx) => {
            this.currentLineX += dx;
            this.updateIntersections();
          },
    });
    this.objects.set('grabHandle', this.grabHandle.element);

    // 10. Pre-allocate intersection points (hidden, neutral)
    for (let i = 0; i < MAX_INTERSECTION_POINTS; i++) {
      const pt = this.board.create('point', [0, 0], {
        visible: false,
        fixed: true,
        size: 5,
        face: 'o',
        fillColor: neutralColor,
        strokeColor: 'transparent',
        highlight: false,
        showInfobox: false,
        withLabel: false,
      }) as GeometryElement;
      this.intersectionPoints.push(pt);
      this.objects.set(`intersection-${i}`, pt);
    }

    // 11. Count label on the topmost intersection point
    if (params.countLabel !== false) {
      this.countLabelAnchor = this.board.create('point', [0, 0], {
        visible: false,
        fixed: true,
      }) as GeometryElement;
      const countLabelCfg = params.countLabelConfig;
      const countLabelConfig: ResolvedLabelConfig = {
        visible: false,
        variant: 'secondary',
        color: 'neutral',
        position: countLabelCfg?.position ?? 'topRight',
        positionIsExplicit: true,
        contentVariant: 'name',
        text: '',
        offset: countLabelCfg?.offset ?? [0, 0],
        spacing: 'normal',
      };
      this.countLabel = bindLabel({
        ctx,
        themeService: this.themeService,
        element: this.countLabelAnchor,
        config: countLabelConfig,
      });
      this.objects.set('countLabel', this.countLabel as unknown as GeometryElement);
    }

    // 12. Feedback label at bottom-right (global text)
    if (params.message !== false) {
      const labelText = isHLT
        ? this.isFunction
          ? 'One-to-one!'
          : 'Not one-to-one!'
        : this.isFunction
          ? 'Is a function!'
          : 'Not a function!';
      const labelAnchor = this.board.create('point', [0, 0], { visible: false }) as GeometryElement;
      const labelConfig: ResolvedLabelConfig = {
        visible: true,
        variant: 'primary',
        color: feedbackColorName as 'sky' | 'pink',
        position: 'canvasTop',
        positionIsExplicit: true,
        contentVariant: 'name',
        text: labelText,
        offset: [0, 0],
        spacing: 'normal',
      };
      this.feedbackLabel = bindLabel({
        ctx,
        themeService: this.themeService,
        element: labelAnchor,
        config: labelConfig,
      });
      this.objects.set('feedbackLabel', this.feedbackLabel as unknown as GeometryElement);
    }

    // 13. Initial intersection computation
    this.updateIntersections();
  }

  override destroy(): void {
    this.grabHandle?.destroy();
    this.grabHandle = undefined;
    this.curveElement = undefined;
    this.lineElement = undefined;
    this.anchorPoint = undefined;
    this.intersectionPoints = [];
    this.feedbackLabel = undefined;
    this.countLabelAnchor = undefined;
    this.countLabel = undefined;
    this.compiledCurveArgs = undefined;
    super.destroy();
  }

  private updateIntersections(): void {
    if (!this.board || !this.compiledCurveArgs) {
      return;
    }

    const intersections =
      this.mode === 'hlt'
        ? computeHorizontalIntersections(this.compiledCurveArgs, this.currentLineY, this.board)
        : computeIntersections(this.compiledCurveArgs, this.currentLineX, this.board);
    const count = intersections.length;

    // Update intersection point positions and visibility
    let topmostIdx = -1;
    let topmostY = -Infinity;
    for (let i = 0; i < MAX_INTERSECTION_POINTS; i++) {
      const pt = this.intersectionPoints[i] as unknown as {
        setPositionDirectly: (type: number, coords: [number, number]) => void;
        setAttribute: (a: Record<string, unknown>) => void;
      };

      if (i < count) {
        pt.setPositionDirectly(1, [intersections[i].x, intersections[i].y]);
        pt.setAttribute({ visible: true });
        if (intersections[i].y > topmostY) {
          topmostY = intersections[i].y;
          topmostIdx = i;
        }
      } else {
        pt.setAttribute({ visible: false });
      }
    }

    // Update count label position and text
    if (this.countLabel && this.countLabelAnchor) {
      if (count > 0 && topmostIdx >= 0) {
        const anchor = this.countLabelAnchor as unknown as {
          setPositionDirectly: (type: number, coords: [number, number]) => void;
        };
        anchor.setPositionDirectly(1, [intersections[topmostIdx].x, intersections[topmostIdx].y]);
        const text = count === 1 ? '1 intersection' : `${count} intersections`;
        this.countLabel.reconfigure({ visible: true, text });
      } else {
        this.countLabel.reconfigure({ visible: false });
      }
    }

    this.board.update();
  }
}
